import { EventTagDot } from '@/features/events/shared/EventTagDot';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  DEACTIVATED_EVENT_TAG_SLUGS,
} from '@/features/events/eventMobileHelpers';
import { getEventTagColors, getEventTagLabel, getTagGroupLabel } from '@/features/events/eventTags';
import { createSentryInputTracker } from '@/lib/analytics/sentry';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { cn } from '@/lib/utils';
import { ChevronDownIcon } from 'lucide-react-native';
import * as React from 'react';
import {
  Pressable,
  View,
} from 'react-native';
import { PosterThumbnailStrip } from './PosterThumbnailStrip';
import type { EventDraft, PitchStepProps } from './types';
import { getFlyerFieldHint } from './utils';

function TagChip({
  tagValue,
  label,
  colors,
  textColor,
  active,
  disabled,
  onPress,
}: {
  tagValue: string;
  label: string;
  colors?: readonly string[];
  textColor?: string;
  active: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  const tagColors = colors?.length ? colors : getEventTagColors(tagValue);
  const activeColor = tagColors[0] ?? DESIGN_TOKENS.colors.accentStrong;
  const activeTextColor = textColor ?? DESIGN_TOKENS.colors.textInverse;

  return (
    <Pressable
      className="flex-row items-center gap-2 rounded-full border px-4 py-3"
      style={{
        opacity: disabled ? 0.42 : 1,
        borderColor: active ? activeColor : DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: active ? activeColor : DESIGN_TOKENS.colors.surfaceAlt,
      }}
      disabled={disabled}
      onPress={onPress}>
      <EventTagDot value={tagValue} size={10} colors={tagColors} />
      <Text
        variant="small"
        className="font-semibold"
        style={{ color: active ? activeTextColor : DESIGN_TOKENS.colors.text }}>
        {label}
      </Text>
    </Pressable>
  );
}

function StepFieldLabel({
  label,
  hint,
  hintTone = 'source',
}: {
  label: string;
  hint?: string;
  hintTone?: 'source' | 'review';
}) {
  return (
    <View className="flex-row items-center gap-2">
      <Text variant="small" className="font-semibold" style={{ color: DESIGN_TOKENS.colors.text }}>
        {label}
      </Text>
      {hint ? (
        <View
          className="rounded-full px-2 py-1"
          style={{
            backgroundColor:
              hintTone === 'review'
                ? 'rgba(239,106,61,0.16)'
                : DESIGN_TOKENS.colors.canvasInset,
          }}>
          <Text
            variant="small"
            style={{
              color: hintTone === 'review' ? '#f59b80' : DESIGN_TOKENS.colors.textSoft,
              fontSize: 11,
            }}>
            {hint}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

type GuestSignalTag = {
  slug: string;
  label: string;
  colors: readonly string[];
  textColor?: string;
  groupLabel?: string;
};

type GuestSignalPreviewModel = {
  title: string;
  detail: string;
  tags: GuestSignalTag[];
};

function GuestSignalPreview({ model }: { model: GuestSignalPreviewModel }) {
  return (
    <View
      className="rounded-[18px] border px-3.5 py-3"
      style={{
        borderColor: DESIGN_TOKENS.colors.borderStrong,
        backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
      }}>
      <View className="flex-row items-start gap-3">
        <View className="mt-1 flex-row">
          {model.tags.slice(0, 4).map((tag, index) => (
            <View
              key={`signal-${tag.slug}`}
              className={index > 0 ? '-ml-1.5' : undefined}
              style={{
                borderWidth: 1,
                borderColor: DESIGN_TOKENS.colors.surfaceAlt,
                borderRadius: 999,
              }}>
              <EventTagDot value={tag.slug} size={12} colors={tag.colors} />
            </View>
          ))}
        </View>
        <View className="min-w-0 flex-1">
          <Text variant="small" className="font-semibold text-muted-foreground">
            Guest signal
          </Text>
          <Text variant="default" className="mt-0.5 font-semibold text-foreground">
            {model.title}
          </Text>
          {model.detail ? (
            <Text variant="small" className="mt-0.5 text-muted-foreground">
              {model.detail}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function getTagCounterState(count: number) {
  if (count >= 5) {
    return { label: '5/5 · limit reached', color: '#ef4e2e' };
  }

  if (count >= 3) {
    return { label: `${count}/5`, color: '#2ecc71' };
  }

  return { label: 'Pick up to 5', color: DESIGN_TOKENS.colors.textSoft };
}

function buildGuestSignalPreview(
  tags: string[],
  ageRestriction: EventDraft['ageRestriction'],
  tagGroups: PitchStepProps['tagGroups']
): GuestSignalPreviewModel {
  const tagMetaBySlug = new Map(
    tagGroups.flatMap((group) =>
      group.tags.map((tag) => [
        tag.slug,
        {
          label: tag.label,
          colors: tag.color ? [tag.color] : getEventTagColors(tag.slug),
          textColor: tag.text_color,
          groupLabel: group.label || getTagGroupLabel(group.category_group),
        },
      ] as const)
    )
  );
  const selectedTags = tags.map((tag) => {
    const meta = tagMetaBySlug.get(tag);
    return {
      slug: tag,
      label: meta?.label ?? getEventTagLabel(tag),
      colors: meta?.colors ?? getEventTagColors(tag),
      textColor: meta?.textColor,
      groupLabel: meta?.groupLabel,
    };
  });
  const selectedGroups = Array.from(
    new Set(selectedTags.flatMap((tag) => tag.groupLabel ?? []))
  );
  const titleTags = selectedTags.slice(0, 3).map((tag) => tag.label);
  const extraCount = Math.max(selectedTags.length - 3, 0);
  const title = [
    titleTags.join(' + '),
    extraCount > 0 ? `+ ${extraCount} more` : null,
  ].filter(Boolean).join(' ');
  const ageLabel =
    ageRestriction === '21_plus'
      ? '21+'
      : ageRestriction === '18_plus'
        ? '18+'
        : null;
  const detailParts = [
    ...selectedGroups.slice(0, 2),
    selectedGroups.length > 2 ? `${selectedGroups.length - 2} more groups` : null,
    ageLabel,
  ].filter(Boolean);

  return {
    title,
    detail: detailParts.join(' · '),
    tags: selectedTags,
  };
}

export function PitchStep({
  draft,
  mode,
  flyerReview,
  tagGroups,
  isLoadingTags,
  tagLoadError,
  isUploadingPoster,
  isImportingFlyer,
  flyerAssistLockedReason,
  posterLimit,
  onChange,
  onPickPosters,
  onRemovePoster,
  onRetryPoster,
  onOpenPosterCrop,
  onReorderPosters,
  onFlyerImport,
}: PitchStepProps) {
  const [focusedField, setFocusedField] = React.useState<'title' | 'description' | null>(null);
  const [isTagsExpanded, setIsTagsExpanded] = React.useState(false);
  const titleInputTracker = React.useMemo(
    () => createSentryInputTracker({ screen: 'event_editor', field: 'title', form: 'event_editor' }),
    []
  );
  const descriptionInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'event_editor',
        field: 'description',
        form: 'event_editor',
      }),
    []
  );
  const canSelectMoreTags = draft.tags.length < 5;
  const tagCounter = getTagCounterState(draft.tags.length);
  const availableTagGroups = React.useMemo(
    () =>
      tagGroups.flatMap((group) => {
        const tags = group.tags.filter((tag) => !DEACTIVATED_EVENT_TAG_SLUGS.has(tag.slug));
        return tags.length > 0 ? [{ ...group, tags }] : [];
      }),
    [tagGroups]
  );
  const collapsedTags = React.useMemo(
    () => availableTagGroups.flatMap((group) => group.tags).slice(0, 8),
    [availableTagGroups]
  );
  const guestSignalPreview = React.useMemo(
    () => buildGuestSignalPreview(draft.tags, draft.ageRestriction, availableTagGroups),
    [availableTagGroups, draft.ageRestriction, draft.tags]
  );

  return (
    <View className="gap-6">
      <View className="gap-3">
        <PosterThumbnailStrip
          posters={draft.posters}
          posterLimit={posterLimit}
          isImportingFlyer={isImportingFlyer}
          onAddPoster={onPickPosters}
          onRemovePoster={onRemovePoster}
          onRetryPoster={onRetryPoster}
          onOpenPosterCrop={onOpenPosterCrop}
          onReorderPosters={onReorderPosters}
        />

        {mode === 'create' ? (
          <Pressable
            onPress={onFlyerImport}
            disabled={Boolean(flyerAssistLockedReason) || isImportingFlyer || isUploadingPoster}
            className="items-center rounded-[18px] border px-4 py-3"
            style={{
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: flyerAssistLockedReason
                ? 'rgba(255,255,255,0.03)'
                : DESIGN_TOKENS.colors.surfaceAlt,
              opacity: flyerAssistLockedReason ? 0.72 : 1,
            }}>
            <Text variant="small" className="font-semibold text-foreground">
              {isImportingFlyer
                ? 'Reading flyer...'
                : flyerAssistLockedReason
                  ? 'Import from flyer — venue Growth feature'
                  : 'Import from flyer — auto-fill with AI'}
            </Text>
          </Pressable>
        ) : null}
        {mode === 'create' && flyerAssistLockedReason ? (
          <Text variant="small" className="text-muted-foreground">
            {flyerAssistLockedReason}
          </Text>
        ) : null}
      </View>

      <View className="gap-2">
        <StepFieldLabel
          label="Event title · Required"
          hint={getFlyerFieldHint(flyerReview, 'title')}
          hintTone={flyerReview?.needsReview.includes('title') ? 'review' : 'source'}
        />
        <Input
          value={draft.title}
          onChangeText={(value) => {
            titleInputTracker.changeText(value);
            onChange({ title: value });
          }}
          onFocus={() => {
            setFocusedField('title');
            titleInputTracker.focus();
          }}
          onBlur={() => {
            setFocusedField((current) => (current === 'title' ? null : current));
            titleInputTracker.blur(draft.title);
          }}
          placeholder="Sunset warehouse set"
          className={cn(
            'h-14 rounded-[22px] border-border-strong bg-surface-alt px-4 text-[15px] leading-5 dark:bg-surface-alt',
            focusedField === 'title' && 'border-primary'
          )}
        />
      </View>

      <View className="gap-2">
        <StepFieldLabel
          label="Description · Required"
          hint={getFlyerFieldHint(flyerReview, 'description')}
          hintTone={flyerReview?.needsReview.includes('description') ? 'review' : 'source'}
        />
        <Textarea
          value={draft.description}
          onChangeText={(value) => {
            descriptionInputTracker.changeText(value);
            onChange({ description: value });
          }}
          onFocus={() => {
            setFocusedField('description');
            descriptionInputTracker.focus();
          }}
          onBlur={() => {
            setFocusedField((current) => (current === 'description' ? null : current));
            descriptionInputTracker.blur(draft.description);
          }}
          placeholder="Describe the vibe..."
          scrollEnabled={false}
          className={cn(
            'min-h-[128px] rounded-[22px] border-border-strong bg-surface-alt p-4 text-[15px] leading-5 dark:bg-surface-alt',
            focusedField === 'description' && 'border-primary'
          )}
        />
      </View>

      <View className="gap-3">
        <View className="flex-row items-center justify-between">
          <StepFieldLabel
            label="Tags · Required"
            hint={getFlyerFieldHint(flyerReview, 'tags')}
            hintTone={flyerReview?.needsReview.includes('tags') ? 'review' : 'source'}
          />
          <Text variant="small" style={{ color: tagCounter.color }}>{tagCounter.label}</Text>
        </View>

        {isLoadingTags ? (
          <Text variant="small" className="text-muted-foreground">Loading tags…</Text>
        ) : null}
        {tagLoadError ? (
          <Text variant="small" style={{ color: '#f59b80' }}>{tagLoadError}</Text>
        ) : null}
        {!isLoadingTags && !tagLoadError && availableTagGroups.length === 0 ? (
          <View
            className="rounded-[16px] border p-3"
            style={{
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surfaceAlt,
            }}>
            <Text variant="small" className="font-semibold text-foreground">
              Tags are not available yet
            </Text>
            <Text variant="small" className="mt-1 text-muted-foreground">
              Try again in a moment. Tags help guests find the event before you publish.
            </Text>
          </View>
        ) : null}

        {!isLoadingTags && !tagLoadError && !isTagsExpanded ? (
          <View className="flex-row flex-wrap gap-2">
            {collapsedTags.map((tag) => {
              const active = draft.tags.includes(tag.slug);
              return (
                <TagChip
                  key={tag.slug}
                  tagValue={tag.slug}
                  label={tag.label}
                  colors={tag.color ? [tag.color] : undefined}
                  textColor={tag.text_color}
                  active={active}
                  disabled={!active && !canSelectMoreTags}
                  onPress={() =>
                    onChange({
                      tags: active
                        ? draft.tags.filter((v) => v !== tag.slug)
                        : [...draft.tags, tag.slug],
                    })
                  }
                />
              );
            })}
            <Pressable
              className="flex-row items-center gap-2 rounded-full border px-4 py-3"
              style={{ borderColor: DESIGN_TOKENS.colors.borderStrong, backgroundColor: DESIGN_TOKENS.colors.surfaceAlt }}
              onPress={() => setIsTagsExpanded(true)}>
              <Text variant="small" className="font-semibold text-muted-foreground">Browse all</Text>
              <Icon as={ChevronDownIcon} size={16} style={{ color: DESIGN_TOKENS.colors.textSoft }} />
            </Pressable>
          </View>
        ) : null}

        {!isLoadingTags && !tagLoadError && isTagsExpanded ? (
          <View className="gap-4">
            {availableTagGroups.map((group) => (
              <View key={group.category_group} className="gap-3">
                <Text variant="small" className="font-semibold text-muted-foreground">
                  {group.label || getTagGroupLabel(group.category_group)}
                </Text>
                <View className="flex-row flex-wrap gap-2">
                  {group.tags.map((tag) => {
                    const active = draft.tags.includes(tag.slug);
                    return (
                      <TagChip
                        key={tag.slug}
                        tagValue={tag.slug}
                        label={tag.label}
                        colors={tag.color ? [tag.color] : undefined}
                        textColor={tag.text_color}
                        active={active}
                        disabled={!active && !canSelectMoreTags}
                        onPress={() =>
                          onChange({
                            tags: active
                              ? draft.tags.filter((v) => v !== tag.slug)
                              : [...draft.tags, tag.slug],
                          })
                        }
                      />
                    );
                  })}
                </View>
              </View>
            ))}
            <Pressable onPress={() => setIsTagsExpanded(false)}>
              <Text variant="small" className="text-primary">Show less</Text>
            </Pressable>
          </View>
        ) : null}

        {draft.tags.length > 0 ? (
          <View className="flex-row flex-wrap gap-2">
            {draft.tags.map((tag) => {
              const colors = getEventTagColors(tag);
              const tagColor = colors[0] ?? DESIGN_TOKENS.colors.borderStrong;
              return (
                <View
                  key={`selected-${tag}`}
                  className="flex-row items-center gap-2 rounded-full border px-3 py-2"
                  style={{
                    borderColor: tagColor,
                    backgroundColor: `${tagColor}18`,
                  }}>
                  <EventTagDot value={tag} size={10} colors={colors} />
                  <Text variant="small" className="font-semibold text-foreground">
                    {getEventTagLabel(tag)}
                  </Text>
                </View>
              );
            })}
          </View>
        ) : null}
      </View>

      {draft.tags.length >= 2 ? <GuestSignalPreview model={guestSignalPreview} /> : null}
    </View>
  );
}
